import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/AuthContext";
import { api } from "@/lib/api";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { LangSwitcher } from "@/components/LangSwitcher";
import { SeoHead } from "@/components/SeoHead";
import { StructuredData, softwareAppSchema, breadcrumbSchema } from "@/components/StructuredData";

const PRICES = {
  pt: {
    pro: { monthly: "R$ 79", yearly: "R$ 790", eq: "R$ 65,83" },
    growth: { monthly: "R$ 197", yearly: "R$ 1.970", eq: "R$ 164,17" },
    agency: { monthly: "R$ 497", yearly: "R$ 4.970", eq: "R$ 414,17" },
    zero: "R$ 0",
  },
  en: {
    pro: { monthly: "US$ 19", yearly: "US$ 190", eq: "US$ 15.83" },
    growth: { monthly: "US$ 39", yearly: "US$ 390", eq: "US$ 32.50" },
    agency: { monthly: "US$ 99", yearly: "US$ 990", eq: "US$ 82.50" },
    zero: "US$ 0",
  },
};

export default function Pricing() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState("");
  const [yearly, setYearly] = useState(false);
  const en = !i18n.language?.startsWith("pt");
  const money = en ? PRICES.en : PRICES.pt;
  const plan = user?.plan || "free";

  const checkout = async (tier) => {
    if (!user) return navigate("/login");
    setLoading(tier);
    try {
      const key = `${tier}_${yearly ? "yearly" : "monthly"}${en ? "_usd" : ""}`;
      const r = await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch {
      toast.error(en ? "Error starting payment. Try again." : "Erro ao iniciar o pagamento. Tente novamente.");
      setLoading("");
    }
  };

  const TIERS = [
    {
      id: "pro",
      name: t("pricing.proName"),
      desc: t("pricing.proDesc"),
      highlight: true,
      features: [
        t("pricing.features.proUnlimited"),
        t("pricing.features.proCampaigns"),
        t("pricing.features.proNoBrand"),
        t("pricing.features.proAi"),
        t("pricing.features.proExport"),
      ],
    },
    {
      id: "growth",
      name: t("pricing.growthName"),
      desc: t("pricing.growthDesc"),
      features: [
        t("pricing.features.growthAll"),
        t("pricing.features.growthReels"),
        t("pricing.features.growthDigest"),
        t("pricing.features.growthMultiLang"),
        t("pricing.features.growthSupport"),
      ],
    },
    {
      id: "agency",
      name: t("pricing.agencyName"),
      desc: t("pricing.agencyDesc"),
      features: [
        t("pricing.features.agencyAll"),
        t("pricing.features.agencyBrands"),
        t("pricing.features.agencyOnboarding"),
        t("pricing.features.agencyWhatsapp"),
        t("pricing.features.agencyPriority"),
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SeoHead pageKey="pricing" />
      <StructuredData data={softwareAppSchema(en ? "en" : "pt")} />
      <StructuredData data={breadcrumbSchema([
        { name: "depoia", url: `https://depoia.com.br/${en ? "en" : "pt"}` },
        { name: en ? "Pricing" : "Preços", url: `https://depoia.com.br/${en ? "en" : "pt"}/pricing` },
      ])} />
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="font-heading font-extrabold text-xl tracking-tight" data-testid="pricing-logo-link">
          depoia<span className="text-[#FF4F00]">.</span>
        </Link>
        <div className="flex items-center gap-3">
          <LangSwitcher />
          <Link
            data-testid="pricing-nav-cta"
            to={user ? "/dashboard" : "/login"}
            className="px-5 py-2.5 bg-[#0A0A0A] text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors"
          >
            {user ? t("common.panel") : t("common.enter")}
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 pt-10 pb-24">
        <p className="text-[#FF4F00] font-semibold text-sm uppercase tracking-widest mb-4">{t("common.prices")}</p>
        <h1 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl max-w-2xl leading-[1.05]">
          {t("pricing.title")}
        </h1>
        <p className="text-zinc-600 mt-6 max-w-xl">{t("pricing.sub")}</p>
        <p className="text-sm text-zinc-500 mt-2" data-testid="trial-note">{t("pricing.trialNote")}</p>

        <div className="inline-flex items-center gap-1 mt-10 p-1 bg-white border border-zinc-200 rounded-full" data-testid="billing-toggle">
          <button
            data-testid="billing-monthly-btn"
            onClick={() => setYearly(false)}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${!yearly ? "bg-[#0A0A0A] text-white" : "text-zinc-600 hover:text-zinc-900"}`}
          >
            {t("pricing.monthly")}
          </button>
          <button
            data-testid="billing-yearly-btn"
            onClick={() => setYearly(true)}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 ${yearly ? "bg-[#0A0A0A] text-white" : "text-zinc-600 hover:text-zinc-900"}`}
          >
            {t("pricing.yearly")} <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${yearly ? "bg-[#FF4F00] text-white" : "bg-orange-100 text-[#FF4F00]"}`}>{en ? "2 months free" : "2 meses grátis"}</span>
          </button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-8 items-start">
          <div className="bg-white border border-zinc-200 rounded-lg p-7" data-testid="plan-free-card">
            <p className="font-heading font-bold text-lg">{t("pricing.freeName")}</p>
            <p className="mt-3"><span className="font-heading font-extrabold text-3xl tracking-tight">{money.zero}</span><span className="text-zinc-500 text-sm">/{en ? "mo" : "mês"}</span></p>
            <p className="text-sm text-zinc-500 mt-2 min-h-[40px]">{t("pricing.freeDesc")}</p>
            <ul className="mt-6 space-y-3">
              {[
                t("pricing.features.freeMedia"),
                t("pricing.features.freeCollect"),
                t("pricing.features.freeCampaigns"),
                t("pricing.features.freeWidget"),
                t("pricing.features.proAi"),
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                  <Check size={16} className="text-[#FF4F00] mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Link
              data-testid="plan-free-cta"
              to={user ? "/dashboard" : "/login"}
              className="mt-8 w-full py-3.5 border border-zinc-300 rounded-full font-semibold text-sm flex items-center justify-center gap-2 text-zinc-800 hover:border-zinc-500 transition-colors"
            >
              {user ? (en ? "Go to dashboard" : "Ir para o painel") : t("landing.ctaStart")} <ArrowRight size={15} />
            </Link>
          </div>

          {TIERS.map((tier) => {
            const p = money[tier.id];
            const dark = tier.highlight;
            const current = plan === tier.id;
            return (
              <div
                key={tier.id}
                data-testid={`plan-${tier.id}-card`}
                className={`rounded-lg p-7 relative ${dark ? "bg-[#0A0A0A]" : "bg-white border border-zinc-200"}`}
              >
                {dark && (
                  <span className="absolute -top-3 left-7 px-3 py-1 bg-[#FF4F00] text-white text-[11px] font-bold rounded-full uppercase tracking-wider">
                    {en ? "Most popular" : "Mais popular"}
                  </span>
                )}
                <p className={`font-heading font-bold text-lg ${dark ? "text-white" : ""}`}>{tier.name}</p>
                {yearly ? (
                  <p className={`mt-3 ${dark ? "text-white" : ""}`} data-testid={`${tier.id}-price-yearly`}>
                    <span className="font-heading font-extrabold text-3xl tracking-tight">{p.yearly}</span>
                    <span className={`text-sm ${dark ? "text-zinc-400" : "text-zinc-500"}`}>/{en ? "yr" : "ano"}</span>
                    <span className={`block text-xs mt-1 ${dark ? "text-zinc-400" : "text-zinc-500"}`}>{t("pricing.monthEq", { value: p.eq })}</span>
                  </p>
                ) : (
                  <p className={`mt-3 ${dark ? "text-white" : ""}`} data-testid={`${tier.id}-price-monthly`}>
                    <span className="font-heading font-extrabold text-3xl tracking-tight">{p.monthly}</span>
                    <span className={`text-sm ${dark ? "text-zinc-400" : "text-zinc-500"}`}>/{en ? "mo" : "mês"}</span>
                  </p>
                )}
                <p className={`text-sm mt-2 min-h-[40px] ${dark ? "text-zinc-400" : "text-zinc-500"}`}>{tier.desc}</p>
                <ul className="mt-6 space-y-3">
                  {tier.features.map((f, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${dark ? "text-zinc-200" : "text-zinc-700"}`}>
                      <Check size={16} className="text-[#FF4F00] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                {current ? (
                  <div data-testid={`plan-${tier.id}-current`} className="mt-8 w-full py-3.5 rounded-full font-semibold text-sm flex items-center justify-center gap-2 text-green-600 border border-green-200 bg-green-50">
                    <Check size={15} /> {t("pricing.current")}
                  </div>
                ) : (
                  <button
                    data-testid={`plan-${tier.id}-cta`}
                    onClick={() => checkout(tier.id)}
                    disabled={!!loading}
                    className={`mt-8 w-full py-3.5 rounded-full font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${
                      dark ? "bg-[#FF4F00] text-white hover:bg-[#E64600]" : "bg-[#0A0A0A] text-white hover:bg-zinc-800"
                    }`}
                  >
                    {loading === tier.id
                      ? (en ? "Redirecting..." : "Redirecionando...")
                      : (plan === "free" ? t("pricing.startTrial") : t("pricing.subscribe"))} <ArrowRight size={15} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-zinc-500 mt-8 text-center">
          {en ? "Secure payment via Stripe. Cancel anytime." : "Pagamento seguro via Stripe. Cancele quando quiser."}
        </p>
      </main>
    </div>
  );
}
